import { useEffect, useMemo, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip, ResponsiveContainer, ReferenceLine, ComposedChart, Legend } from "recharts";
import { TrendingUp, TrendingDown, AlertTriangle, IndianRupee, Calendar, Play } from "lucide-react";

export default function CashForecast() {
    const [weeks, setWeeks] = useState(13);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const run = async () => {
        setLoading(true);
        try { const { data } = await api.get("/reports/cash-forecast", { params: { weeks } }); setData(data); }
        finally { setLoading(false); }
    };
    useEffect(() => { run(); /* eslint-disable-next-line */ }, [weeks]);

    const chart = useMemo(() => (data?.weeks || []).map((w) => ({
        label: w.start.slice(5),
        inflow: w.inflow,
        outflow: -w.outflow,
        closing: w.closing_cash,
    })), [data]);

    const lowestWeek = useMemo(() => {
        if (!data) return null;
        let lowest = null;
        for (const w of data.weeks) {
            if (lowest == null || w.closing_cash < lowest.closing_cash) lowest = w;
        }
        return lowest;
    }, [data]);

    return (
        <>
            <PageHeader
                kicker="REPORTS • PLANNING"
                title="13-week Cash Flow Forecast"
                subtitle="Rolling weekly projection from open AR + AP + recurring + opening cash. Spot the runway gap before it happens."
            />
            <PageBody>
                <div className="bg-card border border-border rounded-md p-4 mb-4 flex flex-wrap items-end gap-3" data-testid="cashflow-toolbar">
                    <div>
                        <Label className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-semibold">Horizon</Label>
                        <Select value={String(weeks)} onValueChange={(v) => setWeeks(Number(v))}>
                            <SelectTrigger className="h-9 w-[140px] mt-1" data-testid="cashflow-weeks"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="4">4 weeks</SelectItem>
                                <SelectItem value="13">13 weeks</SelectItem>
                                <SelectItem value="26">26 weeks</SelectItem>
                                <SelectItem value="52">52 weeks</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="ml-auto">
                        <Button size="sm" onClick={run} data-testid="cashflow-refresh"><Play className="w-3.5 h-3.5 mr-1.5" />Refresh</Button>
                    </div>
                </div>

                {loading && <div className="text-center text-muted-foreground py-12">Building forecast…</div>}
                {data && !loading && (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
                            <Kpi label="Opening cash" value={data.opening_cash} icon={<IndianRupee className="w-3.5 h-3.5" />} />
                            <Kpi label={`Total inflow · ${weeks}w`} value={data.totals.inflow} tone="positive" icon={<TrendingUp className="w-3.5 h-3.5" />} />
                            <Kpi label={`Total outflow · ${weeks}w`} value={data.totals.outflow} tone="destructive" icon={<TrendingDown className="w-3.5 h-3.5" />} />
                            <Kpi label="Projected closing" value={data.totals.closing_cash} tone={data.totals.closing_cash >= 0 ? "positive" : "destructive"} icon={<Calendar className="w-3.5 h-3.5" />} />
                        </div>

                        {(data.overdue.inflow > 0 || data.overdue.outflow > 0) && (
                            <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900 rounded-md p-4 mb-4 text-[12.5px] flex items-start gap-3" data-testid="cashflow-overdue">
                                <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
                                <div>
                                    <div className="font-semibold text-amber-900 dark:text-amber-200">Overdue items not in forecast</div>
                                    <div className="text-amber-800 dark:text-amber-300 mt-0.5">
                                        ₹{data.overdue.inflow.toLocaleString("en-IN")} of receivables and ₹{data.overdue.outflow.toLocaleString("en-IN")} of payables are already past due. They're excluded from the weekly chart but should be collected/paid ASAP.
                                    </div>
                                </div>
                            </div>
                        )}

                        {lowestWeek && lowestWeek.closing_cash < 0 && (
                            <div className="bg-destructive/10 border border-destructive/30 rounded-md p-4 mb-4 text-[12.5px] flex items-start gap-3" data-testid="cashflow-runway-alert">
                                <AlertTriangle className="w-4 h-4 text-destructive mt-0.5 flex-shrink-0" />
                                <div>
                                    <div className="font-semibold text-destructive">Cash runway alert</div>
                                    <div className="mt-0.5">Projected closing cash drops to <span className="font-mono font-medium">{fmtMoney(lowestWeek.closing_cash)}</span> in week of <strong>{lowestWeek.start}</strong>. Consider accelerating collections or deferring payments.</div>
                                </div>
                            </div>
                        )}

                        <div className="bg-card border border-border rounded-md p-4 mb-4">
                            <div className="text-[10px] uppercase tracking-[0.16em] text-gold font-semibold mb-2">Weekly cashflow projection</div>
                            <div className="h-[320px]">
                                <ResponsiveContainer width="100%" height="100%">
                                    <ComposedChart data={chart}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                        <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                                        <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => Math.abs(v) >= 100000 ? `${(v / 100000).toFixed(1)}L` : Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                                        <ReTooltip formatter={(v) => fmtMoney(Math.abs(v))} />
                                        <Legend wrapperStyle={{ fontSize: 11 }} />
                                        <ReferenceLine y={0} stroke="#666" strokeDasharray="2 2" />
                                        <Bar dataKey="inflow" name="Inflow" fill="#1f6c47" />
                                        <Bar dataKey="outflow" name="Outflow" fill="#b1352b" />
                                        <Line type="monotone" dataKey="closing" name="Closing cash" stroke="#a68a56" strokeWidth={2} dot={{ r: 3, fill: "#a68a56" }} />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        <div className="bg-card border border-border rounded-md overflow-hidden" data-testid="cashflow-table">
                            <table className="w-full text-[12.5px]">
                                <thead className="bg-muted/40 border-b border-border">
                                    <tr className="text-left text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-semibold">
                                        <th className="px-4 py-2.5">Week</th>
                                        <th className="px-4 py-2.5">Date range</th>
                                        <th className="px-4 py-2.5 text-right">Inflow</th>
                                        <th className="px-4 py-2.5 text-right">Outflow</th>
                                        <th className="px-4 py-2.5 text-right">Net</th>
                                        <th className="px-4 py-2.5 text-right">Closing cash</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.weeks.map((w) => (
                                        <tr key={w.week} className={`border-b border-border last:border-0 ${w.low_balance_warning ? "bg-destructive/5" : ""}`} data-testid={`cashflow-row-${w.week}`}>
                                            <td className="px-4 py-2 font-medium">W{w.week}</td>
                                            <td className="px-4 py-2 text-muted-foreground font-mono text-[11.5px]">{w.start} → {w.end}</td>
                                            <td className="px-4 py-2 text-right font-mono tabular-nums text-positive">{w.inflow ? fmtMoney(w.inflow) : "—"}</td>
                                            <td className="px-4 py-2 text-right font-mono tabular-nums text-destructive">{w.outflow ? fmtMoney(w.outflow) : "—"}</td>
                                            <td className={`px-4 py-2 text-right font-mono tabular-nums font-medium ${w.net >= 0 ? "text-positive" : "text-destructive"}`}>{fmtMoney(w.net)}</td>
                                            <td className={`px-4 py-2 text-right font-mono tabular-nums font-medium ${w.closing_cash >= 0 ? "" : "text-destructive"}`}>{fmtMoney(w.closing_cash)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </PageBody>
        </>
    );
}

function Kpi({ label, value, tone, icon }) {
    const toneCls = tone === "positive" ? "text-positive" : tone === "destructive" ? "text-destructive" : "";
    return (
        <div className="bg-card border border-border rounded-md p-4">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-semibold">{icon}{label}</div>
            <div className={`font-display text-2xl font-light tracking-tight mt-1 ${toneCls}`}>{fmtMoney(value)}</div>
        </div>
    );
}
